import { useState } from "react"

import { isBefore } from "date-fns"
import {
  CalendarIcon,
  Filter,
  ListChecks,
  Plus,
  Search,
  UserX,
  ZoomIn,
  ZoomOut,
} from "lucide-react"

import { BetaTag } from "@/components/ui/beta-tag"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { formatDayMonthYear } from "@/lib/dateFormats"
import { COVER_BETA_NOTE, type coverBadge } from "@/lib/shiftCover"
import { DEFAULT_HOUR_WIDTH, MAX_HOUR_WIDTH, MIN_HOUR_WIDTH } from "@/lib/timelineScale"
import { toneClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"

export function PlanningTimelineToolbar({
  searchTerm,
  onSearchChange,
  filterHaulageApplicable,
  onFilterHaulageApplicableChange,
  filterEmptyLegOnly,
  onFilterEmptyLegOnlyChange,
  showStandbyRoutes,
  onShowStandbyRoutesChange,
  selectedDate,
  onSelectDate,
  minDate,
  hourWidth,
  onZoomIn,
  onZoomOut,
  onAddRoute,
  showUnscheduledPanel,
  onToggleUnscheduledPanel,
  unscheduledSlotsCount,
  showShiftPanel,
  onToggleShiftPanel,
  shiftCoverBadge,
  canEditRoutes,
  canSeeShiftCover,
  canFilter,
}: {
  searchTerm: string
  onSearchChange: (term: string) => void
  filterHaulageApplicable: boolean
  onFilterHaulageApplicableChange: (value: boolean) => void
  filterEmptyLegOnly: boolean
  onFilterEmptyLegOnlyChange: (value: boolean) => void
  showStandbyRoutes: boolean
  onShowStandbyRoutesChange: (value: boolean) => void
  selectedDate: Date
  onSelectDate: (date: Date) => void
  minDate: Date
  hourWidth: number
  onZoomIn: () => void
  onZoomOut: () => void
  onAddRoute: () => void
  showUnscheduledPanel: boolean
  onToggleUnscheduledPanel: () => void
  unscheduledSlotsCount: number
  showShiftPanel: boolean
  onToggleShiftPanel: () => void
  shiftCoverBadge: ReturnType<typeof coverBadge>
  /** Whether this login may change the plan, and is shown shift cover. */
  canEditRoutes: boolean
  canSeeShiftCover: boolean
  canFilter: boolean
}) {
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false)

  return (
    <div className="bg-white border-b border-palette-jungleGreen200 flex-shrink-0">
      <div className="flex">
        <div className="w-64 border-r border-palette-jungleGreen200 flex-shrink-0">
          <div className="p-2 w-full">
            <div className="relative flex items-center w-full rounded-md border border-palette-jungleGreen200 bg-palette-jungleGreen000 overflow-hidden">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-palette-jungleGreen400 shrink-0" />
              <Input
                placeholder="Search routes..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className={cn(
                  "flex-1 min-w-0 h-8 pl-9 border-0 bg-transparent text-palette-jungleGreen900 placeholder:text-palette-jungleGreen400 text-sm focus-visible:ring-0 focus-visible:ring-offset-0",
                  canFilter ? "pr-9" : "pr-3",
                )}
              />
              {canFilter && (
                <Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "absolute right-0 top-0 bottom-0 h-8 w-8 p-0 rounded-none text-palette-jungleGreen500 hover:bg-palette-jungleGreen100 hover:text-palette-jungleGreen700",
                        (filterEmptyLegOnly || filterHaulageApplicable) &&
                          "bg-blue-50 text-blue-600 hover:bg-blue-100",
                      )}
                      title="Filter"
                    >
                      <Filter size={14} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent variant="menu" align="start">
                    <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-palette-jungleGreen000 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterHaulageApplicable}
                        onChange={(e) => onFilterHaulageApplicableChange(e.target.checked)}
                        className="w-3.5 h-3.5 text-emerald-600 border-palette-jungleGreen300 rounded focus:ring-emerald-500"
                      />
                      <span className="text-xs text-palette-jungleGreen900">
                        Haulage applicable
                      </span>
                    </label>
                    <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-palette-jungleGreen000 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterEmptyLegOnly}
                        onChange={(e) => onFilterEmptyLegOnlyChange(e.target.checked)}
                        className="w-3.5 h-3.5 text-blue-600 border-palette-jungleGreen300 rounded focus:ring-blue-500"
                      />
                      <span className="text-xs text-palette-jungleGreen900">Has empty leg</span>
                    </label>
                    <label className="flex items-center space-x-2 p-1.5 rounded hover:bg-palette-jungleGreen000 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showStandbyRoutes}
                        onChange={(e) => onShowStandbyRoutesChange(e.target.checked)}
                        className="w-3.5 h-3.5 text-slate-600 border-palette-jungleGreen300 rounded focus:ring-slate-500"
                      />
                      <span className="text-xs text-palette-jungleGreen900">
                        Stand-by routes
                      </span>
                    </label>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 bg-white border-palette-jungleGreen200 text-palette-jungleGreen700 hover:bg-palette-jungleGreen000 text-xs"
                >
                  <CalendarIcon size={12} className="mr-1.5" />
                  {formatDayMonthYear(selectedDate)}
                </Button>
              </PopoverTrigger>
              <PopoverContent variant="plain" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && onSelectDate(date)}
                  disabled={(date) => isBefore(date, minDate)}
                  className="bg-white"
                />
              </PopoverContent>
            </Popover>

            <div className="flex items-center gap-0.5 border border-palette-jungleGreen200 rounded-md bg-white">
              <Button
                variant="ghost"
                size="sm"
                onClick={onZoomOut}
                disabled={hourWidth <= MIN_HOUR_WIDTH}
                className="h-7 px-1.5 text-palette-jungleGreen600 hover:bg-palette-jungleGreen100"
              >
                <ZoomOut size={12} />
              </Button>
              <span className="text-xs text-palette-jungleGreen500 px-1.5 min-w-[36px] text-center">
                {Math.round((hourWidth / DEFAULT_HOUR_WIDTH) * 100)}%
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={onZoomIn}
                disabled={hourWidth >= MAX_HOUR_WIDTH}
                className="h-7 px-1.5 text-palette-jungleGreen600 hover:bg-palette-jungleGreen100"
              >
                <ZoomIn size={12} />
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {canEditRoutes && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onAddRoute}
                  className="h-7 bg-white border-palette-jungleGreen200 text-palette-jungleGreen600 hover:bg-palette-jungleGreen000 gap-1.5 text-xs"
                >
                  <Plus size={12} />
                  New Route
                </Button>
                <Button
                  variant={showUnscheduledPanel ? "default" : "outline"}
                  size="sm"
                  onClick={onToggleUnscheduledPanel}
                  className={cn(
                    "h-7 gap-1.5 text-xs relative",
                    showUnscheduledPanel
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-white border-palette-jungleGreen200 text-palette-jungleGreen600 hover:bg-palette-jungleGreen000",
                  )}
                >
                  <ListChecks size={12} />
                  Slots
                  {unscheduledSlotsCount > 0 && (
                    <span
                      className={cn(
                        "absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-medium flex items-center justify-center",
                        showUnscheduledPanel ? "bg-white text-blue-600" : "bg-red-500 text-white",
                      )}
                    >
                      {unscheduledSlotsCount > 99 ? "99+" : unscheduledSlotsCount}
                    </span>
                  )}
                </Button>
              </>
            )}
            {canSeeShiftCover && (
              <Button
                variant={showShiftPanel ? "default" : "outline"}
                size="sm"
                onClick={onToggleShiftPanel}
                className={cn(
                  "h-7 gap-1.5 text-xs relative",
                  showShiftPanel
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-white border-palette-jungleGreen200 text-palette-jungleGreen600 hover:bg-palette-jungleGreen000",
                )}
              >
                <UserX size={12} />
                Cover
                <BetaTag title={COVER_BETA_NOTE} />
                {shiftCoverBadge && (
                  <span
                    className={cn(
                      "absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-medium flex items-center justify-center",
                      showShiftPanel
                        ? "bg-white text-blue-600"
                        : cn(toneClasses(shiftCoverBadge.tone).dot, "text-white"),
                    )}
                  >
                    {shiftCoverBadge.count > 99 ? "99+" : shiftCoverBadge.count}
                  </span>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
